<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Server, ServerOff, HardDrive, ListTodo } from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Badge from '@/components/ui/Badge.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import { formatBytes } from '@/utils/format'
import type { ClusterSummary } from '@/api/clusterSummary'

/**
 * Cluster summary card for the dashboard.
 *
 * Metrics shown when clustering is enabled:
 *  - Online nodes  (members reachable via gossip)
 *  - Offline nodes (members registered but unreachable)
 *  - Disk usage    (data-dir filesystem, with usage bar)
 *  - Cluster load  (total tasks distributed across the cluster)
 *
 * NOTE — "cluster-wide upload speed" is not exposed by the public
 * `/api/cluster/status` endpoint (the backend only returns static counters;
 * speed is a per-task field that would have to be summed via gRPC). The
 * per-node global stat above the card already shows aggregate down/up rates,
 * so we omit a duplicate metric here.
 */

interface Props {
  summary: ClusterSummary | null
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), { loading: false })

const { t } = useI18n()

const diskPercent = computed(() => {
  const s = props.summary
  if (!s?.hasDisk || s.diskTotal <= 0) return 0
  return Math.min(100, Math.round((s.diskUsed / s.diskTotal) * 100))
})

const diskVariant = computed(() =>
  diskPercent.value >= 90 ? 'danger' : diskPercent.value >= 75 ? 'warning' : 'success',
)

const diskBarClass = computed(() => {
  switch (diskVariant.value) {
    case 'danger':
      return 'bg-danger'
    case 'warning':
      return 'bg-warning'
    default:
      return 'bg-success'
  }
})
</script>

<template>
  <Card :title="t('dashboard.clusterStatus')" :subtitle="t('dashboard.subtitle')">
    <template #actions>
      <Badge v-if="summary && !loading" :variant="summary.enabled ? 'success' : 'neutral'" dot>
        {{ summary.enabled ? t('dashboard.clusterEnabled') : t('dashboard.clusterDisabled') }}
      </Badge>
    </template>

    <div v-if="loading" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Skeleton v-for="i in 4" :key="i" height="4rem" block />
    </div>

    <!-- v3.13.67: 集群静默降级横幅 — config 启用了集群但启动失败, 直出原因 -->
    <div
      v-else-if="summary?.degraded"
      class="mb-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning"
      role="alert"
    >
      {{ t('dashboard.clusterDegraded') }}
      <span v-if="summary.degradedReason" class="ml-1 text-fg-muted">({{ summary.degradedReason }})</span>
    </div>

    <div v-else-if="!summary?.enabled" class="py-2 text-sm text-fg-muted">
      {{ t('dashboard.clusterDisabledHint') }}
      <span v-if="summary?.version" class="ml-1 text-fg-subtle">({{ summary.version }})</span>
    </div>

    <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <!-- online -->
      <div class="flex items-center gap-3 rounded-md border border-border bg-bg-elevated/50 p-3">
        <span class="rounded-md bg-success/15 p-2 text-success">
          <Server :size="18" />
        </span>
        <div class="min-w-0">
          <p class="text-xl font-semibold tabular-nums text-fg">{{ summary.onlineNodes }}</p>
          <p class="truncate text-xs text-fg-muted">{{ t('dashboard.nodesOnline') }}</p>
        </div>
      </div>

      <!-- offline -->
      <div class="flex items-center gap-3 rounded-md border border-border bg-bg-elevated/50 p-3">
        <span
          class="rounded-md p-2"
          :class="summary.offlineNodes > 0 ? 'bg-danger/15 text-danger' : 'bg-bg-hover text-fg-subtle'"
        >
          <ServerOff :size="18" />
        </span>
        <div class="min-w-0">
          <p class="text-xl font-semibold tabular-nums text-fg">{{ summary.offlineNodes }}</p>
          <p class="truncate text-xs text-fg-muted">{{ t('dashboard.nodesOffline') }}</p>
        </div>
      </div>

      <!-- disk -->
      <div class="flex items-center gap-3 rounded-md border border-border bg-bg-elevated/50 p-3">
        <span class="rounded-md bg-brand-500/15 p-2 text-brand-600 dark:text-brand-300">
          <HardDrive :size="18" />
        </span>
        <div class="min-w-0 flex-1">
          <p v-if="summary.hasDisk" class="text-xl font-semibold tabular-nums text-fg">
            {{ formatBytes(summary.diskUsed) }}
          </p>
          <p v-else class="text-xl font-semibold text-fg-subtle">—</p>
          <p class="truncate text-xs text-fg-muted">
            {{ t('dashboard.diskUsed') }}
            <span v-if="summary.hasDisk" class="text-fg-subtle">
              / {{ formatBytes(summary.diskTotal) }}
            </span>
          </p>
          <div v-if="summary.hasDisk" class="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-bg-hover">
            <div
              class="h-full rounded-full transition-all duration-base"
              :class="diskBarClass"
              :style="{ width: `${diskPercent}%` }"
            />
          </div>
        </div>
      </div>

      <!-- cluster load -->
      <div class="flex items-center gap-3 rounded-md border border-border bg-bg-elevated/50 p-3">
        <span class="rounded-md bg-warning/15 p-2 text-warning">
          <ListTodo :size="18" />
        </span>
        <div class="min-w-0">
          <p class="text-xl font-semibold tabular-nums text-fg">{{ summary.taskCount }}</p>
          <p class="truncate text-xs text-fg-muted">{{ t('dashboard.clusterLoad') }}</p>
        </div>
      </div>
    </div>

    <template v-if="summary?.enabled && !loading" #footer>
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>{{ t('dashboard.totalNodes') }}: <span class="text-fg">{{ summary.totalNodes }}</span></span>
        <span v-if="summary.hasDisk">
          {{ t('dashboard.diskTotal') }}: <span class="text-fg">{{ formatBytes(summary.diskTotal) }}</span>
        </span>
        <!-- v3.13.55: 本机 ID 从仪表盘移除 (用户反馈) — 配对用的 ID 在 集群 设置页 -->
      </div>
    </template>
  </Card>
</template>

